import { Link } from 'react-router-dom';
import { whyChooseUsFeatures } from '../../../mocks/sharedData';

interface WhyChooseUsProps {
  features?: typeof whyChooseUsFeatures;
}

export default function WhyChooseUs({ features = whyChooseUsFeatures }: WhyChooseUsProps) {
  return (
    <section className="bg-slate-50 py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-200" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-slate-200" />

      {/* Right image accent */}
      <div className="absolute right-0 top-0 bottom-0 w-2/5 hidden xl:block overflow-hidden opacity-25">
        <img
          src="https://public.readdy.ai/ai/img_res/f65e962077c6a64259f4b0fa8bb91dad.jpg"
          alt=""
          className="w-full h-full object-cover object-left"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-50 via-slate-50/60 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-14 items-start">
          {/* Left */}
          <div className="lg:col-span-2">
            <span className="inline-block px-4 py-1.5 rounded-full text-orange-700 text-xs font-bold uppercase tracking-[0.2em] border border-orange-200 bg-orange-50 mb-5">
              Our Advantage
            </span>
            <h2 className="font-display text-5xl lg:text-6xl font-black text-slate-900 leading-tight mb-5">
              Why<br />
              <span className="text-gradient-orange">Choose</span><br />
              <span className="text-slate-900">Secure2Ware</span>
            </h2>
            <div className="w-12 h-1 bg-orange-500 rounded-full mb-6" />
            <p className="text-slate-600 leading-relaxed mb-8 text-base">
              We&apos;re not just another contractor. We&apos;re your long-term security partner —
              accountable for every install and committed to keeping your property protected for years.
            </p>

            {/* Key stats */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { value: '500+', label: 'Properties Secured' },
                { value: '15+', label: 'Years Experience' },
                { value: '60+', label: 'Licensed Techs' },
                { value: '4.9★', label: 'Average Rating' },
              ].map((stat) => (
                <div key={stat.label} className="p-4 rounded-xl border border-slate-200 bg-white">
                  <p className="text-orange-600 font-display font-black text-2xl">{stat.value}</p>
                  <p className="text-slate-500 text-xs mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            <Link
              to="#contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Schedule Free Assessment
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Right feature grid */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className={`rounded-2xl p-6 border transition-all duration-300 cursor-default group hover:border-orange-200 hover:bg-orange-50/50 ${
                  i % 2 === 0
                    ? 'bg-white border-slate-200'
                    : 'bg-orange-50 border-orange-100'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-100 border border-orange-200 flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                    <i className={`${feature.icon} text-orange-600 text-xl`} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-slate-900 text-base mb-1.5">{feature.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
